import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type DownloadQueueStatus = 'loading' | 'complete' | 'error';

export interface DownloadQueueItem {
  id: string;
  title: string;
  status: DownloadQueueStatus;
  helperText?: string;
  downloadUrl?: string;
  fileName?: string;
  createdAt: number;
}

interface DownloadQueueState {
  items: DownloadQueueItem[];
}

const initialState: DownloadQueueState = { items: [] };

const slice = createSlice({
  name: 'downloadQueue',
  initialState,
  reducers: {
    downloadQueued(
      state,
      action: PayloadAction<{ id: string; title: string; helperText?: string }>,
    ) {
      state.items.unshift({
        id: action.payload.id,
        title: action.payload.title,
        helperText: action.payload.helperText,
        status: 'loading',
        createdAt: Date.now(),
      });
    },
    downloadCompleted(
      state,
      action: PayloadAction<{
        id: string;
        helperText?: string;
        downloadUrl?: string;
        fileName?: string;
        // CORP-8360: o nome definitivo do arquivo só é conhecido depois da resposta (o backend
        // manda em Content-Disposition), então o card da fila precisa poder trocar o título.
        title?: string;
      }>,
    ) {
      const item = state.items.find((entry) => entry.id === action.payload.id);
      if (!item) return;
      item.status = 'complete';
      item.helperText = action.payload.helperText;
      item.downloadUrl = action.payload.downloadUrl;
      item.fileName = action.payload.fileName;
      if (action.payload.title) item.title = action.payload.title;
    },
    downloadFailed(state, action: PayloadAction<{ id: string; helperText?: string }>) {
      const item = state.items.find((entry) => entry.id === action.payload.id);
      if (!item) return;
      item.status = 'error';
      item.helperText = action.payload.helperText;
    },
    downloadRemoved(state, action: PayloadAction<{ id: string }>) {
      state.items = state.items.filter((entry) => entry.id !== action.payload.id);
    },
    downloadCompletedCleared(state) {
      state.items = state.items.filter((entry) => entry.status === 'loading');
    },
  },
});

export const {
  downloadQueued,
  downloadCompleted,
  downloadFailed,
  downloadRemoved,
  downloadCompletedCleared,
} = slice.actions;
export default slice.reducer;

// CORP-8108: browsers ignore the anchor `download` attribute for cross-origin URLs (e.g. the
// signed Google Storage links returned by generate-document/PDF resumido) — clicking a detached
// <a> in that case navigates the *current* tab to the file instead of saving it, exactly like
// following a normal link. Only same-origin URLs (our own blob: URLs, e.g. PDF completo's
// downloadRecordDocument) can rely on the anchor trick to force a real Save-As. For anything
// else, open it in a new tab instead so the click at least doesn't blow away the app the user
// was on. A true forced download for those remote files needs the source (GCS signed URL or a
// same-origin backend proxy) to send a `Content-Disposition: attachment` header — that's
// outside what this app's code can control.
function isSameOriginDownloadable(url: string): boolean {
  if (url.startsWith('blob:') || url.startsWith('data:')) return true;
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// CORP-8108 (retorno 24/08): o PDF resumido precisa de window.open no clique (senão o
// bloqueador mata a aba), mas about:blank fica branco até o generate-document voltar.
// Escreve o "Carregando…" na hora, ainda na aba same-origin.
export function openPendingDownloadTab(loadingLabel: string): Window | null {
  const tab = window.open('', '_blank');
  if (!tab) return null;
  const label = escapeHtml(loadingLabel);
  try {
    tab.document.open();
    tab.document.write(`<!doctype html>
<html lang="pt-BR">
  <head>
    <meta charset="utf-8" />
    <title>${label}</title>
    <style>
      html, body { height: 100%; margin: 0; font-family: Arial, sans-serif; background: #f5f5f5; color: #424242; }
      body { display: flex; align-items: center; justify-content: center; }
      .box { text-align: center; }
      .spin { width: 32px; height: 32px; margin: 0 auto 16px; border: 3px solid #e0e0e0; border-top-color: #1976d2; border-radius: 50%; animation: spin .8s linear infinite; }
      @keyframes spin { to { transform: rotate(360deg); } }
    </style>
  </head>
  <body>
    <div class="box">
      <div class="spin"></div>
      <p>${label}</p>
    </div>
  </body>
</html>`);
    tab.document.close();
  } catch {
    // about:blank write can fail in some sandboxes; the tab still exists for location.href later.
  }
  return tab;
}

export function triggerDownload(
  item: Pick<DownloadQueueItem, 'downloadUrl' | 'fileName' | 'title'> & { openedTab?: Window | null },
) {
  if (!item.downloadUrl) return;
  if (!isSameOriginDownloadable(item.downloadUrl)) {
    // CORP-8108 (retorno da QA de 20/08): window.open só escapa do bloqueador de pop-up quando
    // chamado sincronamente dentro do gesto de clique. Como essa URL só existe depois do await
    // de generateDocument, quem chama esta função já perdeu a "user activation" — window.open
    // aqui seria bloqueado silenciosamente (sem erro, nada abre). `openedTab` é a aba que o
    // chamador já abriu em branco, ainda dentro do clique; só navegamos ela agora.
    if (item.openedTab) {
      item.openedTab.location.href = item.downloadUrl;
    } else {
      window.open(item.downloadUrl, '_blank', 'noopener');
    }
    return;
  }
  item.openedTab?.close();
  const anchor = document.createElement('a');
  anchor.href = item.downloadUrl;
  anchor.download = item.fileName ?? item.title;
  anchor.click();
}

export function releaseBlobUrl(item: Pick<DownloadQueueItem, 'downloadUrl'>) {
  if (item.downloadUrl?.startsWith('blob:')) {
    URL.revokeObjectURL(item.downloadUrl);
  }
}
